import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { Button, Steps } from 'antd'
import { useTranslation } from "react-i18next"
import TextArea from 'antd/lib/input/TextArea'
import CheckableTag from 'antd/lib/tag/CheckableTag'
import Toast from "components/dumb/Toast"
import { useIsMobileDevice } from "hooks"
import { feedbackFeelingsArray } from "constants/globalConstants"
import feedbackApi from "services/apis/feedback"
import { storage } from "services/config/storage"
import './style.scss'
import { useGameplayConfig } from "store"

const RESPONSE_TYPES = {
    RATING: "RATING",
    LABELED_SCALE: "LABELED_SCALE",
    TEXT: "TEXT",
    MOOD: "MOOD"
}

const SYSTEM_KEYS = {
    OVERALL_EXPERIENCE_RATING: "overallExperienceRating",
    MOOD_RATING: "moodRating",
    FEEDBACK: "feedback"
}

// keepSession: the end screen still needs the login token and wipes storage itself.
const FeedbackSection = ({ feedbackSubmitted, setFeedbackSubmitted, keepSession = false }) => {
    const [loading, setLoading] = useState(false)
    const [questionsLoading, setQuestionsLoading] = useState(false)
    const [feedbackQuestions, setFeedbackQuestions] = useState([])
    const [customAnswers, setCustomAnswers] = useState({})
    const [values, setValues] = useState({
        feeling: "",
        rating: "",
        comment: ""
    })
    const [error, setError] = useState({})
    const { configLanguageSettings } = useGameplayConfig();
    const { isRtlLayout } = configLanguageSettings;
    console.log('configLanguageSettings', configLanguageSettings)

    const isMobile = useIsMobileDevice()
    const { userId, linkId } = useParams()
    const { t } = useTranslation()

    useEffect(() => {
        const fetchCustomQuestions = async () => {
            if (!linkId) return
            try {
                setQuestionsLoading(true)
                const res = await feedbackApi.getFeedbackQuestions(linkId)
                if (res?.success) {
                    setFeedbackQuestions((res?.data ?? [])
                        .filter(question => question?.isActive !== false)
                        .sort((firstQuestion, secondQuestion) =>
                            (firstQuestion?.sortOrder ?? 0) - (secondQuestion?.sortOrder ?? 0)))
                }
            } catch (e) {
                console.error(e)
            } finally {
                setQuestionsLoading(false)
            }
        }
        fetchCustomQuestions()
    }, [linkId])

    /**
     * This function checks if a value is valid or not.
     * @param {value}
     * @return {Boolean}
     */
    const isValueInvalid = (value) => value === ""

    /**
     * This function checks if all the values are valid or not.
     * @return {isFeedbackValid} - boolean
     */
    const getSystemAnswer = (question) => {
        switch (question.systemKey) {
            case SYSTEM_KEYS.OVERALL_EXPERIENCE_RATING:
                return values.rating === "" ? "" : values.rating + 1
            case SYSTEM_KEYS.MOOD_RATING:
                return values.feeling
            case SYSTEM_KEYS.FEEDBACK:
                return values.comment
            default:
                return ""
        }
    }

    const getQuestionAnswer = (question) =>
        question.isSystem ? getSystemAnswer(question) : customAnswers[question.id]

    const isFeedbackValid = () => {
        const updatedError = {}
        feedbackQuestions.forEach(question => {
            const answer = getQuestionAnswer(question)
            const hasAnswer = answer !== undefined && answer !== null && `${answer}`.trim() !== ""
            if (question.isRequired && !hasAnswer) {
                updatedError[question.id] = true
            }
        })
        setError(updatedError)
        return Object.keys(updatedError).length === 0
    }

    /**
     * This function handles the feedback submission.
     */

    const handleSubmitFeedback = async () => {
        if (isFeedbackValid()) {
            const answers = feedbackQuestions.filter(question => !question.isSystem).reduce((acc, question) => {
                const answer = customAnswers[question.id]
                const hasAnswer = answer !== undefined && answer !== null && `${answer}`.trim() !== ""
                if (!hasAnswer) return acc
                const answerPayload = { questionId: question.id }
                if ([RESPONSE_TYPES.RATING, RESPONSE_TYPES.LABELED_SCALE].includes(question.responseType)) {
                    answerPayload.numericValue = Number(answer)
                } else {
                    answerPayload.textValue = `${answer}`.trim()
                }
                acc.push(answerPayload)
                return acc
            }, [])
            const payload = {
                userId,
                linkId,
                ...(answers.length > 0 && { answers })
            }
            feedbackQuestions.filter(question => question.isSystem).forEach(question => {
                const answer = getSystemAnswer(question)
                const hasAnswer = answer !== undefined && answer !== null && `${answer}`.trim() !== ""
                if (hasAnswer && Object.values(SYSTEM_KEYS).includes(question.systemKey)) {
                    payload[question.systemKey] = answer
                }
            })
            try {
                setLoading(true)
                const res = await feedbackApi.sendFeedback(payload)
                if (res?.success) {
                    if (!keepSession) storage.destroy.all()
                    storage.set.isFeedbackSubmitted(userId)
                    setFeedbackSubmitted(true)
                }
            } catch (e) {
                Toast("error", e?.message)
                if (e?.message?.toLowerCase()?.includes("record already exists")) {
                    storage.set.isFeedbackSubmitted(userId)
                    setFeedbackSubmitted(true)
                }
            } finally {
                setLoading(false)
            }
        }
    }

    const handleCustomAnswerChange = (question, value) => {
        const questionId = question.id
        setCustomAnswers(prevAnswers => ({ ...prevAnswers, [questionId]: value }))
        if (value === undefined || value === null || `${value}`.trim() === "") {
            if (question.isRequired) {
                setError(errState => ({ ...errState, [questionId]: true }))
            }
        } else {
            setError(errState => {
                const updatedError = { ...errState }
                delete updatedError[questionId]
                return updatedError
            })
        }
    }

    const renderCustomQuestion = (question) => {
        const answer = customAnswers[question.id]
        if (question.responseType === RESPONSE_TYPES.TEXT) {
            return <TextArea
                value={answer ?? ""}
                onChange={(e) => handleCustomAnswerChange(question, e.currentTarget.value)}
                style={{ resize: 'none' }}
                rows={4}
                placeholder="Please enter your response"
                className='br-12 mt-8'
            />
        }

        if (question.responseType === RESPONSE_TYPES.LABELED_SCALE) {
            return (
                <div className="labeled-scale-control steps-container p-20 br-12 mt-8">
                    <div className="labeled-scale-points">
                        {Array.from({ length: 5 }, (_, index) => index + 1).map(rating => (
                            <button
                                key={rating}
                                type="button"
                                className={`labeled-scale-point ${Number(answer) === rating ? "selected" : ""}`}
                                onClick={() => handleCustomAnswerChange(question, rating)}
                            >
                                {Number(answer) === rating ? rating : ""}
                            </button>
                        ))}
                    </div>
                    <div className="custom-scale-labels mt-10">
                        {
                            Array.from({ length: 5 }, (_, index) => `${index + 1}`).map(labelKey => (
                                <p key={labelKey} className="m-0 size-12 opacity-70">{question.labels?.[labelKey]}</p>
                            ))
                        }
                    </div>
                </div>
            )
        }

        const range = 10
        return (
            <div className={`flex flex-col justify-center steps-container p-20 br-12 mt-8`}>
                <Steps
                    direction="horizontal"
                    current={answer ? Number(answer) - 1 : -1}
                    onChange={(val) => handleCustomAnswerChange(question, val + 1)}
                    items={Array.from({ length: range }, (_, index) => index + 1)
                        .map(rating => ({
                            icon: <p key={rating} className={`${isMobile ? "size-12" : "size-16"} m-0`}>{rating}</p>,
                        }))
                    }
                    className={`${isMobile ? "mt-14" : ""}`}
                />
            </div>
        )
    }

    /**
     * This function handles the change for input value.
     * Checks validity of value changed as well.
     */
    const handleInputChange = (question, key, value) => {
        const updatedValues = { ...values }
        updatedValues[key] = value
        setValues({ ...updatedValues })
        if (isValueInvalid(value)) {
            if (question.isRequired) {
                setError(errState => ({ ...errState, [question.id]: true }))
            }
        } else {
            setError(errState => {
                const updatedError = { ...errState }
                delete updatedError[question.id]
                return updatedError
            })
        }
        if (key === "rating") {
            setTimeout(() => {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
            }, 600)
        }
    }

    const getSystemQuestionTitle = (question) => {
        switch (question.systemKey) {
            case SYSTEM_KEYS.MOOD_RATING:
                return t("feedback.how_you_feeling")
            case SYSTEM_KEYS.OVERALL_EXPERIENCE_RATING:
                return t("feedback.rate_exp")
            case SYSTEM_KEYS.FEEDBACK:
                return t("feedback.suggestions")
            default:
                return question.questionText
        }
    }

    const getRequiredErrorMessage = (question) => {
        switch (question.systemKey) {
            case SYSTEM_KEYS.MOOD_RATING:
                return t("feedback.feeling_empty")
            case SYSTEM_KEYS.OVERALL_EXPERIENCE_RATING:
                return t("feedback.exp_empty")
            case SYSTEM_KEYS.FEEDBACK:
                return t("feedback.suggestions_empty")
            default:
                return "This response is required"
        }
    }

    const renderSystemQuestion = (question) => {
        if (question.systemKey === SYSTEM_KEYS.MOOD_RATING) {
            return (
                <div className={`tags-container flex items-center flex-wrap mt-8`}>
                    {feedbackFeelingsArray?.map(currentFeeling => (
                        <CheckableTag
                            key={currentFeeling.key}
                            checked={currentFeeling.key === values.feeling}
                            onChange={() => handleInputChange(question, "feeling", currentFeeling.key)}
                        >
                            <span className={`${isRtlLayout ? "ml-6" : "mr-6"}`}><currentFeeling.icon /> </span>
                            <span>{t(`feedback.${currentFeeling.key}`)}</span>
                        </CheckableTag>
                    ))}
                </div>
            )
        }

        if (question.systemKey === SYSTEM_KEYS.OVERALL_EXPERIENCE_RATING) {
            return (
                <div className={`flex flex-col justify-center steps-container p-20 br-12 mt-8`}>
                    <Steps
                        direction="horizontal"
                        current={values.rating}
                        onChange={(value) => handleInputChange(question, "rating", value)}
                        items={Array.from({ length: 10 }, (_, index) => index + 1)
                            .map(rating => ({
                                icon: <p key={rating} className={`${isMobile ? "size-12" : "size-16"} m-0`}>{rating}</p>,
                            }))}
                        className={`${isMobile ? "mt-14" : ""}`}
                    />
                    <div className={`flex items-center justify-between mt-10`}>
                        <p className={`m-0 size-14 opacity-70`}>{t("feedback.not_fun")}</p>
                        <p className={`m-0 size-14 opacity-70`}>{t("feedback.super_fun")}</p>
                    </div>
                </div>
            )
        }

        if (question.systemKey === SYSTEM_KEYS.FEEDBACK) {
            return (
                <TextArea
                    value={values.comment}
                    onChange={(event) => handleInputChange(question, "comment", event.currentTarget.value)}
                    style={{ resize: 'none' }}
                    rows={4}
                    placeholder={t("feedback.suggestions_placeholder")}
                    className='br-12 mt-8'
                />
            )
        }

        return null
    }

    return (
        <div className={`feedback-container flex flex-col items-start justify-center ${feedbackSubmitted ? "feedback-translate" : ""}`}>
            {feedbackQuestions.map(question => (
                <div key={question.id} className={`custom-feedback-question mb-34 p-8`}>
                    <p className='size-16 weight-400 m-0 feedback-category-heading'>
                        {question.isSystem ? getSystemQuestionTitle(question) : question.questionText}
                        {question.isRequired ? " *" : ""}
                    </p>
                    {question.isSystem ? renderSystemQuestion(question) : renderCustomQuestion(question)}
                    {error?.[question.id] ? (
                        <p className="size-12 font-italic text-error m-0 mt-4">
                            {getRequiredErrorMessage(question)}
                        </p>
                    ) : null}
                </div>
            ))}
            <div className={`flex items-center p-8 ${isMobile ? "fixed" : ""} w-full bg-color-primary submit-btn-container`}>
                <Button
                    loading={loading || questionsLoading}
                    onClick={handleSubmitFeedback}
                    className='br-20 size-16 weight-500 submit-feedback-btn'
                >
                    {t("feedback.submit")}
                </Button>
            </div>
        </div>
    )
}

export default FeedbackSection
